import {getTestProps} from '@benchling/js-test-utils';
import {Badge, MantineColor} from '@mantine/core';
import React from 'react';

import {ProcessTaskStatusType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

import {CardSectionRowProps} from '../CardSection';
import {DEFAULT_BADGE_PROPS, HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

function getStatusBadgeLabel(status: ReviewStatus | undefined): string {
  if (status === ReviewStatus.RETRACTED) {
    return 'Retracted';
  }
  return ReviewUtil.getStatusTextFromReview(status) ?? 'In progress';
}

function getStatusBadgeColor(status: ReviewStatus | ProcessTaskStatusType | undefined): MantineColor {
  switch (status) {
    case ReviewStatus.ACCEPTED:
      return 'green';
    case ReviewStatus.REJECTED:
      return 'red';
    case ReviewStatus.NEEDS_REVIEW:
      return 'yellow';
    case ReviewStatus.RETRACTED:
      return 'gray';
    case ProcessTaskStatusType.PENDING:
      return 'gray';
    case ProcessTaskStatusType.IN_PROGRESS:
      return 'blue';
    case ProcessTaskStatusType.COMPLETED:
      return 'green';
    case ProcessTaskStatusType.CANCELLED:
      return 'red';
    case ProcessTaskStatusType.FAILED:
      return 'red';
    case ProcessTaskStatusType.INVALID:
      return 'orange';
    case ProcessTaskStatusType.SKIPPED:
      return 'gray';
    default:
      return 'blue';
  }
}

interface ReviewableListItem {
  id: string;
  name: string;
  modifiedAt: string;
  editUrl: string;
  review?: {status: ReviewStatus} | null;
}

export function reviewableRowProps(item: ReviewableListItem): CardSectionRowProps {
  const icon: BenchlingIconName = APIUtil.isWorksheet(item.id)
    ? 'file-lines-outline'
    : APIUtil.isEntry(item.id)
      ? 'file-outline'
      : 'pen-to-square-outline'; // stage entry

  return {
    id: item.id,
    icon,
    name: item.name,
    modifiedAt: item.modifiedAt,
    href: item.editUrl,
    onClick: () => {
      SidepanelActions.close({autoOpenFile: false});
    },
    rightContent: (
      <Badge
        color={getStatusBadgeColor(item.review?.status)}
        {...DEFAULT_BADGE_PROPS}
        {...getTestProps({
          component: TEST_COMPONENT,
          element: 'statusBadge',
          key: item.review?.status ?? 'none',
        })}
      >
        {getStatusBadgeLabel(item.review?.status)}
      </Badge>
    ),
  };
}

interface ProcessTaskStatus {
  statusType: ProcessTaskStatusType;
  displayName: string;
}

interface TaskListItem {
  id: string;
  name: string;
  modifiedAt: string;
  url: string;
  status: ProcessTaskStatus | null;
}

export function taskRowProps({id, name, modifiedAt, url, status}: TaskListItem): CardSectionRowProps {
  return {
    id,
    icon: 'square-check-outline',
    name,
    modifiedAt,
    href: url,
    onClick: () => {
      SidepanelActions.close({autoOpenFile: false});
    },
    rightContent: (
      <Badge
        color={getStatusBadgeColor(status?.statusType)}
        {...DEFAULT_BADGE_PROPS}
        {...getTestProps({
          component: TEST_COMPONENT,
          element: 'statusBadge',
          key: status?.statusType ?? 'none',
        })}
      >
        {status?.displayName}
      </Badge>
    ),
  };
}
